import type { ISerialize } from '@/objects/ISerialize';
import { TimeSpan, type ITimeSpan } from '@/objects/TimeSpan';
import { formatDateIso } from '@/utilities/dateutils';

export class ServerTime implements ISerialize {
    public ISOTime: string | null;

    public Name: string | null;

    public RequestOffsetMs: number;

    public Time: Date;

    public TimeZone: ITimeSpan;

    public UTC: Date;

    public UTCUnix: number;

    public Version: string | null;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ISOTime?: string | null;
        Name?: string | null;
        RequestOffsetMs?: number;
        Time?: Date;
        TimeZone?: ITimeSpan;
        UTC?: Date;
        UTCUnix?: number;
        Version?: string | null;
    }) {
        this.ISOTime = values.ISOTime ?? null;

        this.Name = values.Name ?? null;

        this.RequestOffsetMs = values.RequestOffsetMs ?? 0;

        this.Time = values.Time ?? new Date('1970-01-01T00:00:00.000');

        this.TimeZone = values.TimeZone ?? TimeSpan.Empty();

        this.UTC = values.UTC ?? new Date('1970-01-01T00:00:00.000');

        this.UTCUnix = values.UTCUnix ?? 0;

        this.Version = values.Version ?? null;
    }

    public serialize(): string {
        return (
            '{' +
            `"ISOTime": ${JSON.stringify(this.ISOTime)}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"RequestOffsetMs": ${JSON.stringify(this.RequestOffsetMs)}` +
            ',' +
            `"Time": ${'"' + formatDateIso(this.Time) + '"'}` +
            ',' +
            `"TimeZone": ${'"' + this.TimeZone?.serialize() + '"'}` +
            ',' +
            `"UTC": ${'"' + formatDateIso(this.UTC) + '"'}` +
            ',' +
            `"UTCUnix": ${JSON.stringify(this.UTCUnix)}` +
            ',' +
            `"Version": ${JSON.stringify(this.Version)}` +
            '}'
        );
    }

    /**
     * Creates an instance of ServerTime from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ISOTime?: string | null;
        Name?: string | null;
        RequestOffsetMs?: number;
        Time?: Date;
        TimeZone?: ITimeSpan;
        UTC?: Date;
        UTCUnix?: number;
        Version?: string | null;
    }): ServerTime {
        return new ServerTime(values);
    }

    public static _FromServerJson(json: any): ServerTime {
        return new ServerTime({
            ISOTime: json.ISOTime ?? '',
            Name: json.Name ?? '',
            RequestOffsetMs: json.RequestOffsetMs,
            Time: json.Time instanceof Date ? json.Time : new Date(json.Time),
            TimeZone: json.TimeZone ? TimeSpan.FromTimeString(json.TimeZone) : TimeSpan.Empty(),
            UTC: json.UTC instanceof Date ? json.UTC : new Date(json.UTC),
            UTCUnix: json.UTCUnix,
            Version: json.Version ?? '',
        });
    }

    /**
     * Creates an instance of ServerTime from another instance of ServerTime.
     * @param other The ServerTime you want to clone
     * @returns A cloned ServerTime
     */
    public static FromOther(other: ServerTime): ServerTime {
        return ServerTime._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ServerTime.FromOther(this);
    }

    static NAME_OF_CLASS = 'ServerTime';
}
